import { useEffect, useMemo } from "react";
import { ChallengeItem } from "../ChallengeItem/ChallengeItem";
import { getChallenges } from "../../../redux/challenges/get-challenges.handler";
import { useDispatch, useSelector } from "../../../hooks";
import { challengesData } from "./challenges";
import styles from "./ChallengesList.module.scss";
import { CategoryEmptyState } from "./CategoryEmptyState/CategoryEmptyState";
import { CategoryDivider } from "./CategoryDivider/CategoryDivider";

// Типы
interface Challenge {
    id?: string | number;
    type: number;
    category: number;
    priority: number;
}

interface ChallengeWithData extends Challenge {
    challengeData: any;
}

interface ChallengesDataItem {
    id: number;
}

enum ChallengeCategory {
    Tutorial = 1,
    Periodical = 2,
    Static = 3,
}

export const ChallengesList = () => {
    const dispatch = useDispatch();
    const { items: challenges, tutorial } = useSelector(store => store.challenges);

    useEffect(() => {
        dispatch(getChallenges());
    }, [dispatch]);

    // Группируем вызовы по категориям с useMemo для оптимизации
    const challengesByCategory = useMemo(() => {
        return challenges.reduce((acc: Record<number, ChallengeWithData[]>, challenge: Challenge) => {
            const challengeData = challengesData.find((item: ChallengesDataItem) => item.id === challenge.type);
            if (!challengeData) return acc;

            if (!acc[challenge.category]) {
                acc[challenge.category] = [];
            }
            
            acc[challenge.category].push({
                ...challenge,
                challengeData
            });
            
            return acc;
        }, {} as Record<number, ChallengeWithData[]>);
    }, [challenges]);

    // Определяем порядок категорий для отображения
    const categoryOrder: ChallengeCategory[] = [
        ChallengeCategory.Tutorial, 
        ChallengeCategory.Periodical, 
        ChallengeCategory.Static
    ];

    const renderChallenges = () => {
        return categoryOrder.map((category: ChallengeCategory, categoryIndex: number) => {
            const categoryChallenges = challengesByCategory[category] || [];
            
            if (category === ChallengeCategory.Tutorial) {
                if (tutorial && tutorial.completed === tutorial.total)
                    return null;
            }
            else {
                if (categoryChallenges.length === 0)
                    return null;
            }            
            
            // Сортируем внутри категории по приоритету
            const sortedCategoryChallenges = [...categoryChallenges]
                .sort((a: ChallengeWithData, b: ChallengeWithData) => a.priority - b.priority);
            
            // Если в категории нет элементов, показываем заглушку
            if (sortedCategoryChallenges.length === 0) {
                return (
                    <div key={`category-${category}`} className={styles.categorySection}>
                        <CategoryDivider category={category} />

                        <CategoryEmptyState 
                            key={`empty-category-${category}`}
                            category={category}
                            isFirst={categoryIndex === 0}
                        />
                    </div>
                );
            }

            return (
                <div key={`category-${category}`} className={styles.categorySection}>
                    <CategoryDivider category={category} />
                    
                    <div className={styles.challengesContainer}>
                        {sortedCategoryChallenges.map((item: ChallengeWithData, index: number) => (
                            <ChallengeItem
                                key={`challenge-${item.id || `${category}-${index}`}`}
                                {...item.challengeData}
                                challenge={item}
                            />
                        ))}
                    </div>
                </div>
            );
        });
    };

    return <div className={styles.container}>{renderChallenges()}</div>;
};